import React, { Component } from 'react';
import { Table, Col } from 'react-bootstrap';
import PortfolioAssetRow from './PortfolioAssetRow';
import PortfolioAssetModal from './PortfolioAssetModal';
import PortfolioFilter from './PortfolioFilter';
import PortfolioTotal from './PortfolioTotal';
import PortfolioSummary from './PortfolioSummary';
import {
  AssetWithCalculatedStats,
  TypeFilter,
  SortOption
} from '../../interfaces/interfaces';

interface Props {
  assets: AssetWithCalculatedStats[];
  deletable: boolean;
}

interface State {
  showModal: boolean;
  assets: AssetWithCalculatedStats[];
  modalAsset?: AssetWithCalculatedStats;
  currentSort: SortOption;
  typeFilter: TypeFilter;
  shouldFilterByReturn: boolean;
  showSummary: boolean;
  returnFilterValue: number;
  extremes: {
    max: number;
    min: number;
  };
}

export default class PortfolioTable extends Component<Props, State> {
  /*
    This component shows the users portfolio (a list of assets).
  */

  constructor(props: Props) {
    super(props);

    // Initialize the state with no equities and modal not showing.
    const extremes = this.getExtremeReturns(props.assets);
    this.state = {
      showModal: false,
      assets: props.assets,
      currentSort: 'transactionTimestamp',
      typeFilter: 'NONE',
      shouldFilterByReturn: false,
      showSummary: false,
      extremes,
      returnFilterValue: 0
    };
  }

  getPortfolioTotal() {
    const filteredAssets = this.getFilteredAssets();
    if (filteredAssets) {
      return <PortfolioTotal assets={filteredAssets} isSummary={false} />;
    }
    return null;
  }

  getExtremeReturns = (assets: AssetWithCalculatedStats[]) => ({
    max: Math.max(...assets.map(asset => asset.return)),
    min: Math.min(...assets.map(asset => asset.return - 1))
  });

  setReturnFilterValue = (returnFilterValue: number) => {
    this.setState({ returnFilterValue });
  };

  setTypeFilter = (typeFilter: TypeFilter) => {
    const showSummary = typeFilter === 'SUMMARY';
    this.setState({ typeFilter, showSummary });
  };

  getFilteredAssets = () =>
    this.state.assets.filter(this.isValidType).filter(this.isOverReturnValue);

  showModal = (asset: AssetWithCalculatedStats) => {
    // Show the modal and set the modal to display the asset clicked.
    this.setState({ showModal: true, modalAsset: asset });
  };

  hideModal = () => {
    this.setState({ showModal: false });
  };

  sortAssets = (sortBy: SortOption) => {
    const { assets } = this.state;
    if (sortBy === this.state.currentSort) {
      assets.reverse();
    } else if (sortBy === 'name') {
      assets.sort((a, b) => a.name?.localeCompare(b?.name));
    } else {
      assets.sort((a, b) => b[sortBy] - a[sortBy]);
    }

    this.setState({
      assets,
      currentSort: sortBy,
      showModal: false
    });
  };

  isValidType = (asset: AssetWithCalculatedStats) =>
    this.state.typeFilter === asset.type ||
    this.state.typeFilter === 'NONE' ||
    (this.state.typeFilter === 'Share' && asset.type === 'Certificate');

  isOverReturnValue = (asset: AssetWithCalculatedStats) =>
    !this.state.shouldFilterByReturn ||
    asset.return >= this.state.returnFilterValue;

  toggleFilterByReturn = (shouldFilterByReturn: boolean) => {
    const value =
      this.state.returnFilterValue ||
      (this.state.extremes.max + this.state.extremes.min) / 2;
    this.setState({
      shouldFilterByReturn,
      returnFilterValue: Math.floor(value)
    });
  };

  render() {
    return (
      <Col md={12}>
        <PortfolioFilter
          toggleFilterByReturn={this.toggleFilterByReturn}
          extremes={this.state.extremes}
          typeFilter={this.state.typeFilter}
          setTypeFilter={this.setTypeFilter}
          setReturnFilterValue={this.setReturnFilterValue}
          shouldFilterByReturn={this.state.shouldFilterByReturn}
          returnFilterValue={this.state.returnFilterValue}
        />
        {this.state.showSummary ? (
          <PortfolioSummary assets={this.props.assets} />
        ) : (
          <div>
            <Table hover responsive size="sm">
              <thead>
                <tr className="portfolio-header">
                  <th
                    className="sortable"
                    onClick={() => this.sortAssets('name')}
                  >
                    Navn <span className="caret" />
                  </th>
                  <th
                    className="sortable hide-on-991px"
                    onClick={() => this.sortAssets('totalPrice')}
                  >
                    Investert <span className="caret" />
                  </th>
                  <th
                    className="sortable hide-on-650px"
                    onClick={() => this.sortAssets('transactionTimestamp')}
                  >
                    Dato inv. <span className="caret" />
                  </th>
                  <th
                    className="sortable hide-on-350px"
                    onClick={() => this.sortAssets('lastUpdatedTimestamp')}
                  >
                    Oppdatert <span className="caret" />
                  </th>
                  <th
                    className="sortable"
                    onClick={() => this.sortAssets('percentChange')}
                  >
                    Siste dag <span className="caret" />
                  </th>
                  <th className="hide-on-374px">Kurs</th>
                  <th
                    className="sortable hide-on-500px"
                    onClick={() => this.sortAssets('return')}
                  >
                    Avk. <span className="caret" />
                  </th>
                  <th
                    className="sortable hide-on-740px"
                    onClick={() => this.sortAssets('percentReturn')}
                  >
                    Avk. % <span className="caret" />
                  </th>
                  <th
                    className="sortable hide-on-580px"
                    onClick={() => this.sortAssets('annualPercentReturn')}
                  >
                    Ann avk. % <span className="caret" />
                  </th>
                  <th
                    className="sortable"
                    onClick={() => this.sortAssets('totalValue')}
                  >
                    Total Verdi <span className="caret" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {this.getFilteredAssets().map(asset => (
                  <PortfolioAssetRow
                    key={asset.id}
                    showModal={() => this.showModal(asset)}
                    asset={asset}
                  />
                ))}
                {this.getPortfolioTotal()}
                <tr className="if-no-results">
                  <td colSpan={3}>Ingen rader funnet..</td>
                  <td className="hide-on-991px" />
                  <td className="hide-on-740px" />
                  <td className="hide-on-650px" />
                  <td className="hide-on-580px" />
                  <td className="hide-on-500px" />
                  <td className="hide-on-374px" />
                  <td className="hide-on-350px" />
                </tr>
              </tbody>
            </Table>
            <PortfolioAssetModal
              show={this.state.showModal}
              hide={this.hideModal}
              asset={this.state.modalAsset}
              deletable={this.props.deletable}
            />
          </div>
        )}
      </Col>
    );
  }
}
